import {useEffect, useMemo, useRef, useState} from 'react';
import {useMutation, useQuery, useQueryClient} from 'react-query';
import {fetchCompany, createCompany} from '../../../../requests/queries/flows';
import {toast} from 'react-toastify';
import {useDispatch, useSelector} from 'react-redux';
import {getUserDefaultCountry} from '../../../../helpers/getUserDefaultCountry';
import {UPDATE_COMPANY_INFO} from '../../../../store/types';

export const useProfiledRiskPaymentModal = () => {
  const {userDetails} = useSelector(state => state?.auth);
  const isProfiledRisk = localStorage.subdomain?.includes('profiledrisk');
  const dispatch = useDispatch();
  const [userCountry, setUserCountry] = useState(null);
  const [showPaymentPopup, setShowPaymentPopup] = useState(false);
  const [isCountryLoading, setIsCountryLoading] = useState(false);

  const createdCompanyIdRef = useRef(null);
  const isCreatingCompanyRef = useRef(false);

  const queryClient = useQueryClient();

  const companyId = userDetails?.company?.id;
  const companyBillingStatus = userDetails?.company?.billing_status;
  const companyName = userDetails?.company?.name || 'Default Company';

  const {
    data: companyData,
    isLoading: isLoadingCompany,
    isFetched: isCompanyFetched,
  } = useQuery(['pr-company-details'], () => fetchCompany(), {
    enabled: !!companyId && !companyBillingStatus,
    retry: false,
    meta: {preventGlobalError: true},
    onSuccess: data => {
      if (data?.entity && isProfiledRisk) {
        const {name, ...rest} = data.entity;
        dispatch({payload: rest, type: UPDATE_COMPANY_INFO});
      }
    },
    onError: async err => {
      if (
        err.response?.data?.error === 'Company does not Exist' &&
        isProfiledRisk &&
        !isCreatingCompanyRef.current &&
        createdCompanyIdRef.current !== companyId
      ) {
        setIsCountryLoading(true);
        await getUserDefaultCountry(setUserCountry);
        setIsCountryLoading(false);
      }
    },
  });

  // Create company mutation
  const {mutate: createCompanyMutation} = useMutation({
    mutationFn: createCompany,
    onSuccess: () => {
      queryClient.invalidateQueries({queryKey: ['pr-company-details']});
      isCreatingCompanyRef.current = false;
    },
    onError: error => {
      toast.error(error?.response?.data?.error || 'Failed to create company.');
      isCreatingCompanyRef.current = false;
    },
  });

  // Reset refs when companyId changes
  useEffect(() => {
    if (
      createdCompanyIdRef.current &&
      createdCompanyIdRef.current !== companyId
    ) {
      createdCompanyIdRef.current = null;
      isCreatingCompanyRef.current = false;
    }
  }, [companyId]);

  // Create company when country is loaded and conditions are met
  useEffect(() => {
    if (
      isProfiledRisk &&
      userCountry?.country &&
      companyId &&
      companyName &&
      createdCompanyIdRef.current !== companyId &&
      !isCreatingCompanyRef.current
    ) {
      isCreatingCompanyRef.current = true;
      createdCompanyIdRef.current = companyId;

      const companyData = {
        company_id: companyId,
        name: companyName,
        country: userCountry.country || 'NG',
      };

      createCompanyMutation(companyData);
    }
  }, [
    isProfiledRisk,
    userCountry?.country,
    companyId,
    companyName,
    createCompanyMutation,
  ]);

  // Manage payment popup visibility
  useEffect(() => {
    if (!isLoadingCompany && isCompanyFetched) {
      if (
        companyData &&
        !companyData?.entity?.billing_status &&
        !companyData?.entity?.trial_status
      ) {
        setShowPaymentPopup(true);
      } else {
        setShowPaymentPopup(false);
      }
    }
  }, [isLoadingCompany, isCompanyFetched, companyData]);

  const billingStatus = useMemo(
    () => companyData?.entity?.billing_status,
    [companyData],
  );
  const trialStatus = useMemo(
    () => companyData?.entity?.trial_status,
    [companyData],
  );

  const isLoading = isLoadingCompany || isCountryLoading;

  return {
    billingStatus,
    trialStatus,
    isLoading,
    closePaymentPopup: () => setShowPaymentPopup(false),
    showPaymentPopup,
    setShowPaymentPopup,
    companyData: companyData?.entity,
  };
};
